import { isEmpty } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const VerificationInputWrapper = styled.div`
  display: flex;
  gap: 14px;
  width: 100%;
  max-width: 360px;

  input {
    flex: 1;
    min-width: 0;
    height: 48px;
    text-align: center;
    font-size: 20px;
    line-height: 24px;
    color: #4d5156;
    border: none;
    border-radius: 10px;
    outline: none;
    box-shadow:
      0px 0px 0px 1px rgba(71, 58, 31, 0.16),
      0px 1px 3px 0px rgba(71, 58, 31, 0.06);

    &:focus {
      box-shadow:
        0px 0px 0px 1px #0e141b,
        0px 1px 3px 0px rgba(14, 20, 27, 0.16),
        0px 0px 0px 4px rgba(14, 20, 27, 0.12);
    }
  }
`;

const InputCell = ({
  isFocused,
  onFocus,
  onType,
  goBack,
  value,
}: {
  isFocused: boolean;
  onFocus: () => void;
  goBack: () => void;
  onType: (newValue: string) => void;
  value: string;
}) => {
  const ref = useMemo(() => {
    return React.createRef<HTMLInputElement>();
  }, []);

  useEffect(() => {
    if (ref.current && isFocused) {
      ref.current.focus();
    }
  }, [isFocused, ref]);

  return (
    <input
      onFocus={onFocus}
      ref={ref}
      value={value}
      onChange={(e) => {
        onType(e.target.value);
      }}
      onKeyDown={(e) => {
        switch (e.key) {
          case "Backspace":
            if (isEmpty(value)) {
              e.preventDefault();
              goBack();
            }
            break;
          default:
            break;
        }
      }}
    />
  );
};

type VerificationInputProps = {
  values: string[];
  setParentValues: (values: string[]) => void;
  length: number;
  onComplete: (values: string[]) => Promise<void>;
  resetValues: () => void;
};

const VerificationInput = ({
  length,
  onComplete,
  values,
  setParentValues,
  resetValues,
}: VerificationInputProps) => {
  const setValues = useCallback(
    (newValues: any) => {
      setParentValues(newValues);
      if (newValues.find(isEmpty) === undefined) {
        onComplete(newValues).then(() => {
          resetValues();
          setCurrentIndex(0);
        });
      }
    },
    [onComplete, resetValues, setParentValues],
  );

  const updateValues = useCallback(
    (index: any, newValue: any) => {
      setValues(values.map((value, i) => (i === index ? newValue : value)));
    },
    [setValues, values],
  );

  const [currentIndex, setCurrentIndex] = useState(0);

  const onType = useCallback(
    (oldValue: any) => (newValue: string) => {
      const chars = newValue.toUpperCase().split("");
      if (chars.length === 0) {
        updateValues(currentIndex, "");
        return;
      }
      if (!isEmpty(oldValue)) {
        return;
      }
      let char_idx = 0;
      let value_idx = currentIndex;
      const oldValues = [...values];
      while (char_idx < chars.length && value_idx < length) {
        oldValues[value_idx] = chars[char_idx];
        value_idx++;
        char_idx++;
      }
      setValues(oldValues);
      setCurrentIndex(Math.min(value_idx, length - 1));
    },
    [currentIndex, length, setValues, updateValues, values],
  );

  const goBack = useCallback(() => {
    const prevIndex = Math.max(currentIndex - 1, 0);
    updateValues(prevIndex, "");
    setCurrentIndex(prevIndex);
  }, [currentIndex, updateValues]);

  const inputs = useMemo(() => {
    return values.map((value, i) => {
      return (
        <InputCell
          key={i}
          isFocused={currentIndex === i}
          onFocus={() => {
            setCurrentIndex(i);
          }}
          value={value}
          goBack={goBack}
          onType={onType(value)}
        />
      );
    });
  }, [currentIndex, goBack, onType, values]);

  return <VerificationInputWrapper>{inputs}</VerificationInputWrapper>;
};

export default VerificationInput;
